/**
 * The app-tour catalogue.
 *
 * Editorial rules, because a tour that breaks them is the thing people
 * complain about:
 *
 *  - **Few stops.** Nine is the ceiling, and a user with no plans sees four.
 *    A tour is an orientation, not a manual; the last stop hands people the
 *    row that restarts it rather than trying to cover what it left out.
 *  - **One idea per stop**, two sentences at most.
 *  - **Say what you'd DO there, not what it is.** "Plans" on a nav item
 *    already says "Plans". A stop earns its place by naming the moment
 *    you'd click — "find out what changed", "hand a form to Ellie".
 *  - **Every stop must be able to stand alone.** Each in-plan step carries
 *    its own `navigate`, so Back/Next from any direction still lands on a
 *    screen where the anchor exists. A step that borrows the previous
 *    step's navigation breaks the moment someone presses Back.
 *  - **Never gate anything on finishing.** Esc, the ×, and clicking the
 *    dimmed backdrop all end the tour, and it never starts on its own.
 *
 * Steps whose `appliesTo` is false are dropped when the tour starts, so the
 * "n of m" counter always reflects the stops this person will actually see.
 * `appliesTo` should mirror the conditions under which the anchor RENDERS —
 * a step filtered up-front is a clean counter; a step that times out at
 * runtime is a 2.5-second stall followed by a renumbering.
 */

import { navigationRef } from '../navigation/navigationRef';
import { TOUR_ANCHORS } from './anchors';
import type { TourStep } from './types';

/** Navigate to a top-level tab, resetting its stack to the index screen. */
function goToTab(tab: 'Plans' | 'Settings') {
  if (!navigationRef.isReady()) return;
  const screen = tab === 'Plans' ? 'ProjectsList' : 'SettingsHome';
  try {
    (navigationRef as any).navigate('Main', { screen: tab, params: { screen } });
  } catch {
    /* best-effort — the overlay skips the step if the anchor never appears */
  }
}

/** Navigate to a project-scoped screen inside the Plans stack. */
function goToProjectScreen(
  screen: string,
  projectId: string | number | null,
  extraParams?: Record<string, unknown>,
) {
  if (!navigationRef.isReady() || !projectId) return;
  try {
    (navigationRef as any).navigate('Main', {
      screen: 'Plans',
      params: { screen, params: { projectId, ...extraParams } },
    });
  } catch {
    /* best-effort */
  }
}

export const TOUR_STEPS: TourStep[] = [
  {
    key: 'sidebar',
    anchor: TOUR_ANCHORS.sidebar,
    title: 'Everything starts here',
    body: 'This rail is the whole map — hover it and the labels appear. Wherever you are, it takes one click to get anywhere else.',
    placement: 'right',
    // The sidebar is present in every desktop shell, including a project
    // screen such as Notes. Do not replace the launcher while its click is
    // still settling; the following Plans step performs the navigation.
  },
  {
    key: 'plans',
    anchor: TOUR_ANCHORS.navPlans,
    title: 'One plan per person',
    body: 'Each person you care for gets a plan: their documents, medications, appointments, care team, and history in one place — so nothing lives in your head alone.',
    placement: 'right',
    navigate: () => goToTab('Plans'),
  },
  {
    key: 'ellie',
    anchor: TOUR_ANCHORS.navEllie,
    title: 'Ask across every plan',
    body: '"Who has an appointment this week?" Ellie here answers from all your plans at once, and can act on them — schedule, draft, remind.',
    placement: 'right',
    appliesTo: (a) => a.hasAccountChat && !a.isPartner,
  },
  {
    key: 'new-plan',
    anchor: TOUR_ANCHORS.newPlan,
    title: 'Start with what you have',
    body: 'Answer a short intake, describe the person in your own words, or just upload any document — a care summary, an IEP — and Eleplan reads the details out of it.',
    placement: 'bottom',
    navigate: () => goToTab('Plans'),
    // Only when there's nothing to tour inside: once a plan exists, the
    // in-plan stops below carry the value and this one is noise.
    appliesTo: (a) => !a.isPartner && !a.hasProjects,
  },
  {
    key: 'plan-setup',
    anchor: TOUR_ANCHORS.planSetupCard,
    title: 'The checklist knows what matters next',
    body: 'This card ranks the highest-value things this plan is still missing. The more the plan knows, the more specific every answer about them gets.',
    placement: 'top',
    navigate: (a) => goToProjectScreen('ProjectHome', a.firstProjectId),
    appliesTo: (a) => a.hasProjects && !a.isPartner,
  },
  {
    key: 'project-care',
    anchor: TOUR_ANCHORS.navProjectCare,
    title: 'The day-to-day lives in Care',
    body: 'Calendar, medications, and reminders for this person. Mark a dose taken, and everyone who should know, knows.',
    placement: 'right',
    navigate: (a) => goToProjectScreen('ProjectHome', a.firstProjectId),
    appliesTo: (a) => a.hasProjects && !a.isPartner,
  },
  {
    key: 'project-docs',
    anchor: TOUR_ANCHORS.navProjectDocs,
    title: 'Upload it once, stop re-reading it',
    body: 'Files, the encrypted Vault, surveys, and benefits. Anything you upload is read and summarised, and the appointments and medications inside it are offered back for your approval.',
    placement: 'right',
    navigate: (a) => goToProjectScreen('ProjectHome', a.firstProjectId),
    appliesTo: (a) => a.hasProjects && !a.isPartner,
  },
  {
    key: 'project-team',
    anchor: TOUR_ANCHORS.navProjectTeam,
    title: 'You don’t have to be the only one',
    body: 'Invite family, caregivers, or a case manager with per-area permissions — each person sees exactly what you decide, and so does their Ellie.',
    placement: 'right',
    navigate: (a) => goToProjectScreen('ProjectHome', a.firstProjectId),
    appliesTo: (a) => a.hasProjects && !a.isPartner,
  },
  {
    key: 'project-ellie',
    anchor: TOUR_ANCHORS.navProjectEllie,
    title: 'Ellie, scoped to this one person',
    body: 'Inside a plan, Ellie answers only from this person’s record — and cites the document it drew from, so you can check it.',
    placement: 'right',
    navigate: (a) => goToProjectScreen('ProjectHome', a.firstProjectId),
    appliesTo: (a) => a.hasProjects && !a.isPartner,
  },
  {
    key: 'restart',
    anchor: TOUR_ANCHORS.settingsTourRow,
    title: 'That’s the tour',
    body: 'Settings holds your profile, security, notifications, and billing — and this row, so you can run the tour again whenever you want.',
    placement: 'top',
    navigate: () => goToTab('Settings'),
  },
];
